// Live's clip animations (#266, spec docs/specs/editor/animations.md): a clip's list of presets,
// each In, Out or (a combo) neither, with its Timing, Easing and the preset's own settings. This
// module is the presets' one table: each one's name, the clips it is offered for, the settings
// its popover shows, and its motion as stops (from live's names, settings and the stops live
// declares, never live's code). Read by the Animations group and popover, and by the engine
// (`engine/animate.ts`), which plays the stops.
import type {
  AnimationAxis,
  AnimationDirection,
  AnimationEasing,
  AnimationMode,
  AnimationOrigin,
  AnimationPreset,
  BlockMode,
  KeyframeProperty,
} from "../../api/generated/model";

/** One of a clip's animations, as the contract keeps it (`ClipAnimation`). */
export interface Animation {
  id: string;
  preset: AnimationPreset;
  /** In or Out; null for a combo. An Out ends at the clip's end (its delay is not used). */
  mode: AnimationMode | null;
  durationMs: number;
  delayMs: number;
  /** Cycles inside the duration: they do not lengthen it. */
  iterations: number;
  easing: AnimationEasing;
  direction: AnimationDirection;
  /** Slide's distance, live's pixels. */
  distance: number;
  /** Angle Wipe's angle, degrees. */
  angle: number;
  axis: AnimationAxis;
  origin: AnimationOrigin;
  /** Edge Crop's margins, live's pixels. */
  xMargin: number;
  yMargin: number;
  /** Rect Expand's and Edge Crop's corners, live's pixels. */
  borderRadius: number;
  /** How much of a mask preset shows at its start, 0 to 0.9. */
  initial: number;
  /** Block Reveal's: how its block passes, its size in Slide mode (0.1 to 1), its colours. */
  blockMode: BlockMode;
  blockSize: number;
  blockColor: string;
  invertedColor: string;
  /** A by-word or by-character preset's Stagger, seconds; null for the preset's own. */
  stagger: number | null;
  /** Custom's stops (0 % and 100 % among them) and live's Mirror switch. */
  keyframes: readonly Keyframe[];
  mirror: boolean;
}

/** What a stop sets: offsets in live's pixels (x, y) and degrees (angle), the rest multipliers. */
export type AnimatedProp =
  | "x"
  | "y"
  | "scale"
  | "scaleX"
  | "scaleY"
  | "angle"
  | "opacity"
  | "blur"
  | "brightness"
  | "saturation"
  | "motionBlur"
  | "mirror"
  | "maskProgress"
  // Custom's own (part 3): the size, a mask box, a mask's corners, the letters, the crop.
  | "width"
  | "height"
  | "maskWidth"
  | "maskHeight"
  | "maskRadius"
  | "maskBorderRadius"
  | "tracking"
  | "lineHeight"
  | "fontSize"
  | "cropX"
  | "cropY"
  | "cropWidth"
  | "cropHeight";

/** Each property at rest: where a stop leaves it unset, it is this (live's rule). */
export const AT_REST: Readonly<Record<AnimatedProp, number>> = {
  x: 0,
  y: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  angle: 0,
  opacity: 1,
  blur: 0,
  brightness: 1,
  saturation: 1,
  motionBlur: 0,
  mirror: 0,
  maskProgress: 1,
  width: 0,
  height: 0,
  maskWidth: 0,
  maskHeight: 0,
  maskRadius: 0,
  maskBorderRadius: 0,
  tracking: 0,
  lineHeight: 1,
  fontSize: 0,
  cropX: 0,
  cropY: 0,
  cropWidth: 0,
  cropHeight: 0,
};

/** A Custom property (live's "Add Property" list): its key, label, slider range and default. */
export interface KeyframeField {
  key: KeyframeProperty;
  label: string;
  min: number;
  max: number;
  step: number;
}

/** Live's Custom properties, in its order, with its sliders' ranges; each starts at rest. */
export const KEYFRAME_FIELDS: readonly KeyframeField[] = [
  { key: "x", label: "X Position", min: -2000, max: 2000, step: 1 },
  { key: "y", label: "Y Position", min: -2000, max: 2000, step: 1 },
  { key: "width", label: "Width", min: -1000, max: 1000, step: 1 },
  { key: "height", label: "Height", min: -1000, max: 1000, step: 1 },
  { key: "scale", label: "Scale", min: 0, max: 3, step: 0.1 },
  { key: "scaleX", label: "Scale X", min: 0, max: 3, step: 0.1 },
  { key: "scaleY", label: "Scale Y", min: 0, max: 3, step: 0.1 },
  { key: "angle", label: "Rotation", min: -360, max: 360, step: 1 },
  { key: "opacity", label: "Opacity", min: 0, max: 1, step: 0.01 },
  { key: "blur", label: "Blur", min: 0, max: 100, step: 1 },
  { key: "brightness", label: "Brightness", min: 0, max: 5, step: 0.1 },
  { key: "saturation", label: "Saturation", min: 0, max: 3, step: 0.1 },
  { key: "motionBlur", label: "Motion Blur", min: 0, max: 500, step: 1 },
  { key: "maskWidth", label: "Mask Width", min: -1000, max: 1000, step: 1 },
  { key: "maskHeight", label: "Mask Height", min: -1000, max: 1000, step: 1 },
  { key: "maskRadius", label: "Mask Radius", min: 0, max: 500, step: 1 },
  { key: "maskProgress", label: "Mask Progress", min: 0, max: 1, step: 0.01 },
  { key: "maskBorderRadius", label: "Mask Border Radius", min: 0, max: 500, step: 1 },
  { key: "tracking", label: "Tracking", min: -0.5, max: 2, step: 0.01 },
  { key: "lineHeight", label: "Line Height", min: 0.5, max: 3, step: 0.01 },
  { key: "fontSize", label: "Font Size", min: -200, max: 200, step: 1 },
  { key: "cropX", label: "Crop X", min: -2000, max: 2000, step: 1 },
  { key: "cropY", label: "Crop Y", min: -2000, max: 2000, step: 1 },
  { key: "cropWidth", label: "Crop Width", min: -2000, max: 2000, step: 1 },
  { key: "cropHeight", label: "Crop Height", min: -2000, max: 2000, step: 1 },
];

/** One stop of a Custom animation, as the contract keeps it. */
export interface Keyframe {
  at: number;
  values: Readonly<Partial<Record<KeyframeProperty, number>>>;
}

/** The most stops a Custom animation keeps (the contract's `MAX_KEYFRAMES`). */
export const MAX_KEYFRAMES = 20;

/** A stop: `at` percent of a cycle, and what it sets. */
export interface Stop {
  at: number;
  values: Readonly<Partial<Record<AnimatedProp, number>>>;
}

/** How a mask preset shows the clip (`maskProgress` 0 shows nothing, 1 all of it). */
export type MaskShape = "wipe" | "angle" | "center" | "circle" | "rect" | "edge";

/** The settings a preset's popover shows, in live's order. */
export type PresetField =
  | "direction"
  | "slide"
  | "textSlide"
  | "block"
  | "wordSlide"
  | "stagger"
  | "angle"
  | "axis"
  | "origin"
  | "margins"
  | "borderRadius"
  | "initial";

export type PresetGroup = "transition" | "text" | "combo" | "custom";

export interface PresetInfo {
  /** Its tile's name. */
  label: string;
  /** Its row's name where live's differs ("Slide Caption"), and its tile's spoken name where two
   * tiles share a label. */
  rowLabel?: string;
  spoken?: string;
  /** Transitions are offered for every clip, In or Out; text presets for a text; combos for
   * videos and stills. */
  group: PresetGroup;
  fields: readonly PresetField[];
  mask: MaskShape | null;
  /** The motion's stops, In or Out; a combo's are its own. */
  stops(animation: Animation, mode: AnimationMode | null): readonly Stop[];
}

const two = (from: Stop["values"], to: Stop["values"]): readonly Stop[] => [
  { at: 0, values: from },
  { at: 100, values: to },
];

/** In from `from` to `to`, or Out back from `to` to `from`. */
const inOut =
  (from: Stop["values"], to: Stop["values"], out?: readonly Stop[]) =>
  (_: Animation, mode: AnimationMode | null): readonly Stop[] =>
    mode === "out" ? (out ?? two(to, from)) : two(from, to);

/** A text preset's Out: its In played backwards (live's stops mirrored in time). */
const backwards = (stops: readonly Stop[]): readonly Stop[] =>
  [...stops].reverse().map((stop) => ({ at: 100 - stop.at, values: stop.values }));

/** A text preset whose Out is its In backwards. */
const textStops =
  (stops: (animation: Animation) => readonly Stop[]) =>
  (animation: Animation, mode: AnimationMode | null): readonly Stop[] =>
    mode === "out" ? backwards(stops(animation)) : stops(animation);

/** Live's text Slide: Direction is where it travels (the transition's is where it comes from). */
function textSlideFrom(animation: Animation): Stop["values"] {
  switch (animation.direction) {
    case "left":
      return { x: 50 };
    case "right":
      return { x: -50 };
    case "top":
      return { y: 50 };
    case "bottom":
      return { y: -50 };
  }
}

/** What a word or a letter of a by-word or by-character preset starts from (In) or goes to (Out):
 * offsets in live's pixels, turn and slant in degrees, the rest multipliers; at rest otherwise. */
export interface ElementPose {
  alpha: number;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  skewX: number;
  blur: number;
}

export const POSE_AT_REST: ElementPose = { alpha: 1, x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0, skewX: 0, blur: 0 };

/**
 * Live's by-word and by-character presets: each word (or letter) from its pose to rest, one after
 * another by the Stagger. `tweenS` is how long one takes (live's own; the animation's duration
 * else); `scaleFrames` a scale through stops (Pop By Word); `scramble` letters in a shuffled order
 * three at a time; `lineMask` each line clipped to its own band (Slide Mask Word).
 */
export interface StaggerInfo {
  unit: "word" | "character";
  each: number;
  from: (animation: Animation) => Partial<ElementPose>;
  tweenS?: number;
  scaleFrames?: readonly [number, number][];
  scramble?: boolean;
  lineMask?: boolean;
  /** Live's yoyo: each piece goes to rest and back again inside its time (Wave By Word's In). */
  yoyo?: boolean;
}

/** Where a word slides from: the Direction's side, by the Distance. */
function wordSlideFrom(animation: Animation): Partial<ElementPose> {
  const d = animation.distance;
  switch (animation.direction) {
    case "left":
      return { x: -d };
    case "right":
      return { x: d };
    case "top":
      return { y: -d };
    case "bottom":
      return { y: d };
  }
}

/** Live's by-word and by-character presets' motion (its preset tables, read for behaviour). */
export const STAGGERS: Readonly<Partial<Record<AnimationPreset, StaggerInfo>>> = {
  "appear-by-word": { unit: "word", each: 0.05, from: () => ({ alpha: 0 }), tweenS: 0.001 },
  "fade-by-word": { unit: "word", each: 0.05, from: () => ({ alpha: 0 }) },
  "slide-by-word": { unit: "word", each: 0.2, from: (a) => ({ alpha: 0, ...wordSlideFrom(a) }) },
  "slide-mask-word": { unit: "word", each: 0.2, from: (a) => ({ alpha: 0, ...wordSlideFrom(a) }), lineMask: true },
  "char-fade": { unit: "character", each: 0.05, from: () => ({ alpha: 0 }) },
  "char-slide-up": { unit: "character", each: 0.05, from: () => ({ alpha: 0, y: 50 }) },
  "char-typewriter": { unit: "character", each: 0.05, from: () => ({ alpha: 0 }), tweenS: 0.001 },
  "char-fade-in": { unit: "character", each: 0.05, from: () => ({ alpha: 0, scaleX: 0.5, scaleY: 0.5 }) },
  "char-scramble": {
    unit: "character",
    each: 0.05,
    from: () => ({ alpha: 0, scaleX: 0, scaleY: 0 }),
    tweenS: 0.05,
    scramble: true,
  },
  "pop-by-word": {
    unit: "word",
    each: 0.05,
    from: () => ({ scaleX: 0, scaleY: 0 }),
    scaleFrames: [
      [0, 0],
      [50, 1.2],
      [100, 1],
    ],
  },
  "scale-fade-by-word": { unit: "word", each: 0.08, from: () => ({ alpha: 0, scaleX: 0.7, scaleY: 0.7 }) },
  "bounce-by-word": { unit: "word", each: 0.1, from: () => ({ alpha: 0, y: -30, scaleX: 0.8, scaleY: 0.8 }) },
  "rotate-by-word": { unit: "word", each: 0.08, from: () => ({ alpha: 0, rotation: -10 }) },
  "fade-rotate-by-word": { unit: "word", each: 0.1, from: () => ({ alpha: 0, rotation: 90 }) },
  "skew-by-word": { unit: "word", each: 0.08, from: () => ({ alpha: 0, skewX: 45 }) },
  "wave-by-word": { unit: "word", each: 0.12, from: () => ({ alpha: 0, y: 20 }), yoyo: true },
  "blur-by-word": { unit: "word", each: 0.08, from: () => ({ alpha: 0, blur: 10 }) },
  "drop-soft-by-word": { unit: "word", each: 0.09, from: () => ({ alpha: 0, y: -60, scaleY: 2 }) },
  "elastic-pop-by-word": { unit: "word", each: 0.07, from: () => ({ alpha: 0, scaleX: 0, scaleY: 0 }) },
  "flip-up-by-word": { unit: "word", each: 0.1, from: () => ({ alpha: 0, skewX: -20 }) },
  "spin-by-word": { unit: "word", each: 0.09, from: () => ({ alpha: 0, rotation: -180, scaleX: 0.5, scaleY: 0.5 }) },
  "stretch-by-word": { unit: "word", each: 0.08, from: () => ({ alpha: 0, scaleX: 1.5, scaleY: 0.5 }) },
  "reveal-zoom-by-word": { unit: "word", each: 0.1, from: () => ({ alpha: 0, scaleX: 1.4, scaleY: 1.4 }) },
  "float-wave-by-word": { unit: "word", each: 0.15, from: () => ({ alpha: 0, y: 25 }) },
};

/**
 * A by-word or by-character preset's row in the table: each word or letter moves (`STAGGERS`);
 * the text's body does not. Its settings are live's: Stagger for the 15 its popover draws,
 * Direction and Distance for the word slides.
 */
const staggered = (label: string, settings: "stagger" | "wordSlide" | "none" = "stagger"): PresetInfo => ({
  label,
  group: "text",
  fields: settings === "none" ? [] : [settings],
  mask: null,
  stops: () => [],
});

/** A mask preset: the clip shown from `initial` to all of it (In) or back (Out). */
const revealed = inOut({ maskProgress: 0 }, { maskProgress: 1 });

/** Where a Slide comes from (In) or goes to (Out). */
function slideOffset(animation: Animation): Stop["values"] {
  const d = animation.distance;
  switch (animation.direction) {
    case "left":
      return { x: -d, y: 0 };
    case "right":
      return { x: d, y: 0 };
    case "top":
      return { x: 0, y: -d };
    case "bottom":
      return { x: 0, y: d };
  }
}

/** A combo's stops, every one with live's mirrored tiling on. */
const combo =
  (stops: readonly [number, Stop["values"]][]) =>
  (): readonly Stop[] =>
    stops.map(([at, values]) => ({ at, values: { ...values, mirror: 1 } }));

const MASK_FIELDS: Record<MaskShape, readonly PresetField[]> = {
  wipe: ["direction", "initial"],
  angle: ["angle", "initial"],
  center: ["axis", "initial"],
  circle: ["origin", "initial"],
  rect: ["origin", "borderRadius", "initial"],
  edge: ["origin", "margins", "borderRadius", "initial"],
};

const mask = (label: string, shape: MaskShape): PresetInfo => ({
  label,
  group: "transition",
  fields: MASK_FIELDS[shape],
  mask: shape,
  stops: revealed,
});

/** Live's presets, in its popover's order. */
export const PRESETS: Readonly<Record<AnimationPreset, PresetInfo>> = {
  fade: {
    label: "Fade",
    group: "transition",
    fields: [],
    mask: null,
    // Live's Fade In grows from 0.9 as it shows; its Out only fades.
    stops: inOut({ opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1 }, two({ opacity: 1 }, { opacity: 0 })),
  },
  zoom: {
    label: "Zoom",
    group: "transition",
    fields: [],
    mask: null,
    stops: inOut({ scale: 0, opacity: 0 }, { scale: 1, opacity: 1 }),
  },
  slide: {
    label: "Slide",
    group: "transition",
    fields: ["slide"],
    mask: null,
    stops: (animation, mode) => {
      const away = { ...slideOffset(animation), opacity: 0 };
      const home = { x: 0, y: 0, opacity: 1 };
      return mode === "out" ? two(home, away) : two(away, home);
    },
  },
  blur: {
    label: "Blur",
    group: "transition",
    fields: [],
    mask: null,
    stops: inOut({ blur: 20, opacity: 0 }, { blur: 0, opacity: 1 }),
  },
  wipe: mask("Wipe", "wipe"),
  "circle-reveal": mask("Circle Reveal", "circle"),
  "rect-expand": mask("Rect Expand", "rect"),
  "angle-wipe": mask("Angle Wipe", "angle"),
  "center-expand": mask("Center Expand", "center"),
  "edge-crop": {
    ...mask("Edge Crop", "edge"),
    // Live's Edge Crop runs the other way: its In crops in and shrinks, and holds; its Out uncrops.
    stops: (_, mode) =>
      mode === "out"
        ? two({ maskProgress: 0 }, { maskProgress: 1 })
        : two({ maskProgress: 1, scale: 1 }, { maskProgress: 0, scale: 0.85 }),
  },
  pulse: {
    label: "Pulse",
    group: "transition",
    fields: [],
    mask: null,
    stops: () => [
      { at: 0, values: { scale: 1 } },
      { at: 25, values: { scale: 0.9 } },
      { at: 50, values: { scale: 1 } },
      { at: 75, values: { scale: 0.9 } },
      { at: 100, values: { scale: 1 } },
    ],
  },
  "appear-by-word": staggered("Appear", "none"),
  "fade-by-word": staggered("Fade By Word"),
  "pop-caption": {
    label: "Pop",
    group: "text",
    fields: [],
    mask: null,
    stops: textStops(() => [
      { at: 0, values: { scale: 0.7 } },
      { at: 50, values: { scale: 1.1 } },
      { at: 100, values: { scale: 1 } },
    ]),
  },
  "bounce-caption": {
    label: "Bounce",
    group: "text",
    fields: [],
    mask: null,
    stops: textStops(() => [
      { at: 0, values: { y: 0, scaleY: 1, scaleX: 1 } },
      { at: 20, values: { y: 30, scaleY: 0.8, scaleX: 1.2 } },
      { at: 40, values: { y: 15, scaleY: 1, scaleX: 1 } },
      { at: 60, values: { y: 20, scaleY: 0.9, scaleX: 1.1 } },
      { at: 80, values: { y: 10, scaleY: 1, scaleX: 1 } },
      { at: 100, values: { y: 0, scaleY: 1, scaleX: 1 } },
    ]),
  },
  "scale-caption": {
    label: "Scale",
    group: "text",
    fields: [],
    mask: null,
    stops: textStops(() => two({ scale: 0 }, { scale: 1 })),
  },
  "slide-caption": {
    label: "Slide",
    rowLabel: "Slide Caption",
    spoken: "Slide text",
    group: "text",
    fields: ["textSlide"],
    mask: null,
    stops: textStops((animation) => two(textSlideFrom(animation), { x: 0, y: 0 })),
  },
  "slide-by-word": staggered("Slide By Word", "wordSlide"),
  "slide-mask-word": staggered("Slide Mask Word", "wordSlide"),
  "block-reveal": {
    label: "Block Reveal",
    group: "text",
    fields: ["block"],
    mask: null,
    // A block over each line (`engine/block-shape.ts`); the text itself does not move.
    stops: () => [],
  },
  "char-fade": staggered("Char Fade", "none"),
  "char-slide-up": staggered("Char Slide Up"),
  "char-typewriter": staggered("Char Typewriter"),
  "char-fade-in": staggered("Char Fade In"),
  "char-scramble": staggered("Char Scramble", "none"),
  "pop-by-word": staggered("Pop By Word"),
  "scale-fade-by-word": staggered("Scale Fade By Word"),
  "bounce-by-word": staggered("Bounce By Word"),
  "rotate-by-word": staggered("Rotate By Word", "none"),
  "fade-rotate-by-word": staggered("Fade Rotate By Word"),
  "skew-by-word": staggered("Skew By Word"),
  "wave-by-word": staggered("Wave By Word"),
  "blur-by-word": staggered("Blur By Word", "none"),
  "drop-soft-by-word": staggered("Drop Soft By Word"),
  "elastic-pop-by-word": staggered("Elastic Pop By Word"),
  "flip-up-by-word": staggered("Flip Up By Word"),
  "spin-by-word": staggered("Spin By Word", "none"),
  "stretch-by-word": staggered("Stretch By Word", "none"),
  "reveal-zoom-by-word": staggered("Reveal Zoom By Word"),
  "float-wave-by-word": staggered("Float Wave By Word"),
  "combo-zoom-1": {
    label: "Combo Zoom 1",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 2.5, motionBlur: 8 }],
      [30, { scale: 1.2, motionBlur: 0 }],
      [60, { scale: 1, motionBlur: 0 }],
      [100, { scale: 2.5, motionBlur: 8 }],
    ]),
  },
  "combo-zoom-2": {
    label: "Combo Zoom 2",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 0.7, motionBlur: 8 }],
      [30, { scale: 1, motionBlur: 0 }],
      [60, { scale: 1, motionBlur: 0 }],
      [100, { scale: 0.7, motionBlur: 8 }],
    ]),
  },
  "combo-pendulum-1": {
    label: "Combo Pendulum 1",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { x: -250, blur: 7, angle: 5 }],
      [45, { x: -40, blur: 1, angle: 2 }],
      [55, { x: 0, blur: 0, angle: 0 }],
      [60, { x: 0, blur: 0, angle: 0 }],
      [85, { x: -40, blur: 0, angle: -2 }],
      [100, { x: -250, blur: 0, angle: -5 }],
    ]),
  },
  "combo-pendulum-2": {
    label: "Combo Pendulum 2",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { x: 250, blur: 7, angle: -5 }],
      [45, { x: 40, blur: 1, angle: -2 }],
      [55, { x: 0, blur: 0, angle: 0 }],
      [60, { x: 0, blur: 0, angle: 0 }],
      [85, { x: 40, blur: 0, angle: 2 }],
      [100, { x: 250, blur: 0, angle: 5 }],
    ]),
  },
  "combo-right-distort": {
    label: "Combo Right Distort",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 3, angle: -15, blur: 5 }],
      [40, { scale: 1.5, angle: -6, blur: 1 }],
      [60, { scale: 1, angle: 0, blur: 0 }],
      [70, { scale: 1, angle: 0, blur: 0 }],
      [85, { scale: 0.95 }],
      [100, { scale: 0.8 }],
    ]),
  },
  "combo-left-distort": {
    label: "Combo Left Distort",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 2.5, blur: 5, angle: 0 }],
      [45, { scale: 1, blur: 0 }],
      [50, { scale: 1, blur: 0 }],
      [60, { angle: -3 }],
      [75, { angle: -6 }],
      [100, { scale: 0.7, angle: -6, blur: 0 }],
    ]),
  },
  "combo-wobble": {
    label: "Combo Wobble",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 1.2, angle: 15, x: -400, blur: 5 }],
      [6, { scale: 1, angle: 0, x: 0, blur: 0 }],
      [18, { angle: -10, x: -100 }],
      [30, { angle: -10, x: 0 }],
      [35, { angle: 0 }],
      [45, { angle: 5 }],
      [55, { angle: 0 }],
      [60, { angle: 0, blur: 0, x: 0 }],
      [100, { scale: 2.5, blur: 5 }],
    ]),
  },
  "combo-spinning-top-1": {
    label: "Combo Spinning Top 1",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 3, blur: 5 }],
      [25, { scale: 1, blur: 0, angle: 0 }],
      [55, { scale: 0.7, angle: 8 }],
      [100, { scale: 0.7, angle: 90 }],
    ]),
  },
  "combo-spinning-top-2": {
    label: "Combo Spinning Top 2",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 0.8, angle: -90 }],
      [40, { scale: 0.8, angle: -15 }],
      [60, { scale: 1, angle: 0, blur: 0 }],
      [100, { scale: 3, blur: 5 }],
    ]),
  },
  "combo-sway-out": {
    label: "Combo Sway Out",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 3, blur: 5 }],
      [50, { scale: 1, blur: 0, angle: 0 }],
      [100, { scale: 3, blur: 5, angle: 45 }],
    ]),
  },
  "combo-bounce-1": {
    label: "Combo Bounce 1",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 1.2, blur: 5 }],
      [18, { scale: 1, blur: 0, angle: 0 }],
      [50, { scale: 1.5, angle: -5 }],
      [55, { scale: 1.5, angle: -5 }],
      [100, { scale: 0.9, angle: 0 }],
    ]),
  },
  "combo-sway-in": {
    label: "Combo Sway In",
    group: "combo",
    fields: [],
    mask: null,
    stops: combo([
      [0, { scale: 3, blur: 5, angle: -45 }],
      [50, { scale: 1, blur: 0, angle: 0 }],
      [100, { scale: 3, blur: 5 }],
    ]),
  },
  custom: {
    label: "Custom",
    group: "custom",
    fields: [],
    mask: null,
    // The clip's own stops, mirrored on every one when its switch is on.
    stops: (animation) =>
      [...animation.keyframes]
        .sort((one, other) => one.at - other.at)
        .map((stop) => ({ at: stop.at, values: { ...stop.values, mirror: animation.mirror ? 1 : 0 } })),
  },
};

/** The presets offered for a clip of `kind`, in live's order: transitions, then a text's own
 * presets for a text, or the combos for a video or a still. */
export function presetsFor(kind: "video" | "image" | "text"): readonly AnimationPreset[] {
  return (Object.keys(PRESETS) as AnimationPreset[]).filter((preset) => {
    const group = PRESETS[preset].group;
    if (group === "custom") return false;
    return group === "transition" || (group === "text") === (kind === "text");
  });
}

/** Live's Easing list, in its order. */
export const EASINGS: readonly { value: AnimationEasing; label: string }[] = [
  { value: "linear", label: "Linear" },
  { value: "slow", label: "Slow" },
  { value: "ease-in-quad", label: "Ease In Quad" },
  { value: "ease-out-quad", label: "Ease Out Quad" },
  { value: "ease-in-out-quad", label: "Ease In Out Quad" },
  { value: "ease-in-cubic", label: "Ease In Cubic" },
  { value: "ease-out-cubic", label: "Ease Out Cubic" },
  { value: "ease-in-out-cubic", label: "Ease In Out Cubic" },
  { value: "ease-in-back", label: "Ease In Back" },
  { value: "ease-out-back", label: "Ease Out Back" },
];

/** The contract's bounds (`MAX_ANIMATIONS` and the rest). */
export const MAX_ANIMATIONS = 20;
export const MAX_ANIMATION_MS = 300_000;
export const MAX_ITERATIONS = 100;

/**
 * A preset as live's popover starts it: In (none for a combo), 1000 ms (a combo the whole
 * clip), no delay, one cycle, Linear, and its settings at live's defaults.
 */
export function newAnimation(id: string, preset: AnimationPreset, clipMs: number): Animation {
  const isCombo = PRESETS[preset].group === "combo";
  const noMode = isCombo || preset === "custom";
  return {
    id,
    preset,
    mode: noMode ? null : "in",
    durationMs: isCombo ? Math.min(Math.round(clipMs), MAX_ANIMATION_MS) : 1000,
    delayMs: 0,
    iterations: 1,
    easing: "linear",
    direction: "left",
    distance: 300,
    angle: 45,
    axis: "vertical",
    origin: "center",
    xMargin: 100,
    yMargin: 100,
    borderRadius: 0,
    initial: 0,
    blockMode: "reveal",
    blockSize: 0.3,
    blockColor: "#FFFFFF", // raw-color: live's Block Color, a value
    invertedColor: "#000000", // raw-color: live's Inverted Text, a value
    stagger: null,
    keyframes: preset === "custom" ? [{ at: 0, values: {} }, { at: 100, values: {} }] : [],
    mirror: false,
    // Live's word slides come from the right, 50 px.
    ...(preset === "slide-by-word" || preset === "slide-mask-word" ? { direction: "right" as const, distance: 50 } : {}),
  };
}

/** Live's duration badge: "500ms" under a second, else seconds to a tenth ("1s", "1.3s"). */
export function durationLabel(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${Math.round(ms / 100) / 10}s`;
}

/** Where an animation starts on its clip: an Out ends at the clip's end. */
export function startOf(animation: Animation, clipMs: number): number {
  return animation.mode === "out" ? Math.max(0, clipMs - animation.durationMs) : animation.delayMs;
}
